import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { 
  Check, X, Mic, Mail, Calendar, LogOut, 
  Shield, Loader2, ArrowLeft, MessageSquare 
} from "lucide-react";
import { Link } from "react-router-dom";
import { User } from "@supabase/supabase-js";

interface CommunityStory {
  id: string;
  name: string;
  email: string;
  story: string;
  wants_podcast_interview: boolean;
  is_approved: boolean;
  created_at: string;
}

const Admin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [filter, setFilter] = useState<"all" | "pending" | "approved" | "podcast">("all");

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
        if (!session?.user) {
          navigate("/auth");
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (!session?.user) {
        navigate("/auth");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  // Check admin role
  useEffect(() => {
    if (user) {
      supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle()
        .then(({ data, error }) => {
          if (error) {
            console.error("Error checking admin role:", error);
            setIsAdmin(false);
          } else {
            setIsAdmin(!!data);
          }
        });
    }
  }, [user]);

  const { data: stories, isLoading } = useQuery({
    queryKey: ["admin-stories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("community_stories")
        .select("*")
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data as CommunityStory[];
    },
    enabled: isAdmin === true,
  });

  const updateStoryMutation = useMutation({
    mutationFn: async ({ id, is_approved }: { id: string; is_approved: boolean }) => {
      const { error } = await supabase
        .from("community_stories")
        .update({ is_approved })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-stories"] });
      queryClient.invalidateQueries({ queryKey: ["community-stories"] });
      toast({ title: "Story updated successfully" });
    },
    onError: (error) => {
      console.error("Error updating story:", error);
      toast({ title: "Failed to update story", variant: "destructive" });
    },
  });

  const deleteStoryMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("community_stories")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-stories"] });
      queryClient.invalidateQueries({ queryKey: ["community-stories"] });
      toast({ title: "Story deleted" });
    },
    onError: (error) => {
      console.error("Error deleting story:", error);
      toast({ title: "Failed to delete story", variant: "destructive" });
    },
  });

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const filteredStories = stories?.filter((story) => {
    switch (filter) {
      case "pending":
        return !story.is_approved;
      case "approved":
        return story.is_approved;
      case "podcast":
        return story.wants_podcast_interview;
      default:
        return true;
    }
  });

  if (isAdmin === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-story-hope" />
      </div>
    );
  }

  if (isAdmin === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-6">
            <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Access Denied</h2>
            <p className="text-gray-500 mb-6">
              You don't have admin privileges. Contact the site owner to request access.
            </p>
            <div className="space-y-2">
              <Button asChild variant="outline" className="w-full">
                <Link to="/">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Home
                </Link>
              </Button>
              <Button onClick={handleSignOut} variant="ghost" className="w-full text-gray-500">
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-gray-500 hover:text-gray-700">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="font-serif text-xl font-semibold text-story-germany">Admin Panel</h1>
              <p className="text-sm text-gray-500">Manage community stories & podcast requests</p>
            </div>
          </div>
          <Button onClick={handleSignOut} variant="outline" size="sm">
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter("all")}>
            <CardContent className="pt-4 pb-3">
              <p className="text-2xl font-bold text-story-germany">{stories?.length || 0}</p>
              <p className="text-sm text-gray-500">Total Stories</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter("pending")}>
            <CardContent className="pt-4 pb-3">
              <p className="text-2xl font-bold text-orange-500">
                {stories?.filter(s => !s.is_approved).length || 0}
              </p>
              <p className="text-sm text-gray-500">Pending Review</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter("approved")}>
            <CardContent className="pt-4 pb-3">
              <p className="text-2xl font-bold text-green-500">
                {stories?.filter(s => s.is_approved).length || 0}
              </p>
              <p className="text-sm text-gray-500">Approved</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter("podcast")}>
            <CardContent className="pt-4 pb-3">
              <p className="text-2xl font-bold text-story-hope">
                {stories?.filter(s => s.wants_podcast_interview).length || 0}
              </p>
              <p className="text-sm text-gray-500">Podcast Requests</p>
            </CardContent>
          </Card>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { key: "all", label: "All Stories" },
            { key: "pending", label: "Pending" },
            { key: "approved", label: "Approved" },
            { key: "podcast", label: "Podcast Requests" },
          ].map((tab) => (
            <Button
              key={tab.key}
              variant={filter === tab.key ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(tab.key as typeof filter)}
              className={filter === tab.key ? "bg-story-germany" : ""}
            >
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Stories List */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-story-hope" />
          </div>
        ) : filteredStories?.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No stories found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredStories?.map((story, index) => (
              <motion.div
                key={story.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className={`${!story.is_approved ? "border-l-4 border-l-orange-400" : ""}`}>
                  <CardContent className="p-6">
                    <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-story-germany">{story.name}</h3>
                          {story.wants_podcast_interview && (
                            <Badge className="bg-story-hope/20 text-story-germany">
                              <Mic className="w-3 h-3 mr-1" />
                              Podcast
                            </Badge>
                          )}
                          <Badge variant={story.is_approved ? "default" : "secondary"}>
                            {story.is_approved ? "Approved" : "Pending"}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {story.email}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(story.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {!story.is_approved ? (
                          <Button
                            size="sm"
                            onClick={() => updateStoryMutation.mutate({ id: story.id, is_approved: true })}
                            className="bg-green-500 hover:bg-green-600"
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateStoryMutation.mutate({ id: story.id, is_approved: false })}
                          >
                            Unapprove
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            if (confirm("Delete this story permanently?")) {
                              deleteStoryMutation.mutate(story.id);
                            }
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{story.story}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Admin;
